import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { nodeEnv } from 'environment'

import { useAppSelector } from 'app-redux/hooks'
import { selectMaindeckCards, selectCommanderCards, useCanEditDeck } from 'app-redux/deck/selectors'
import { useComponentDidMount } from 'hooks/componentDidMount'

import {
  hideTagRecommendationsKeyForNow as hideTemp,
  permanentlyHideTagRecommendationsKey as hidePerm,
} from 'components/accountSettingsPage/BrowserSpecificSiteSettings'

const REMINDER_MENU_DELAY = nodeEnv === 'production' ? 30000 : 10

export const useShowTagRecsMenu = (): [boolean, () => void] => {
  const isMounted = useComponentDidMount()

  const canEditDeck = useCanEditDeck()

  const [allowMenuToShow, setAllowMenuToShow] = useState<boolean>(false)
  const [showMenu, setShowMenu] = useState<boolean>(false)

  const deckId = useAppSelector(state => state.deck.id)
  const mainDeckCards = useAppSelector(selectMaindeckCards)
  const commanderCards = useAppSelector(selectCommanderCards)
  const deckTags = useAppSelector(state => state.deck.deckTags)
  const legalityStatus = useAppSelector(state => state.deck.legalityStatus)

  const [{ [hideTemp]: hideTagRecTemp, [hidePerm]: hideTagRecPerm }, setCookies] = useCookies([hideTemp, hidePerm])

  const handleCloseTagReminder = () => {
    setShowMenu(false)

    // Hide for 7 days
    setCookies(hideTemp, true, {
      path: '/',
      expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7),
    })
  }

  // We don't want to reminder menu popping up too quickly. This delay prevents that, no matter how many
  // changes are made to the deck.
  useEffect(() => {
    // If deck tags are set when the page loads, never show the reminder menu even if the conditions for it are met
    if (deckTags.length) return

    // Never show menu if the user cannot edit the deck
    if (!canEditDeck) return

    setTimeout(() => setAllowMenuToShow(true), REMINDER_MENU_DELAY)
  }, [])

  // If the menu is ever closed for any reason after it opens, hide it for 7 days
  useEffect(() => {
    if (!showMenu && isMounted)
      setCookies(hideTemp, true, {
        path: '/',
        expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7),
      })
  }, [showMenu])

  useEffect(() => {
    if (!allowMenuToShow) return

    if (hideTagRecTemp === 'true') return setShowMenu(false)
    if (hideTagRecPerm === 'true') return setShowMenu(false)
    if (!isMounted) return setShowMenu(false)
    if (legalityStatus !== 'legal') return setShowMenu(false)
    if (deckTags.length !== 0) return setShowMenu(false)
    if (!deckId) return setShowMenu(false) // Sandbox

    setShowMenu(true)
  }, [mainDeckCards, commanderCards, hideTagRecTemp, hideTagRecPerm, deckTags, legalityStatus])

  return [showMenu, handleCloseTagReminder]
}
